if (!customElements.get('ht-reward-ladder')) {
  const money = (cents) => {
    const currency = window.Shopify?.currency?.active || 'EUR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', { style: 'currency', currency }).format(
        cents / 100
      );
    } catch (error) {
      return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
    }
  };

  const escape = (text) =>
    String(text).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

  const ARROW =
    '<svg class="ht-rl__gain-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M7 17L17 7M9 7h8v8"/></svg>';

  // "Nein, danke" on the gift picker lasts for the browser session.
  const dismissKey = (reward) => `ht-rl-dismissed:${reward}`;
  const isDismissed = (reward) => {
    try {
      return sessionStorage.getItem(dismissKey(reward)) === '1';
    } catch (error) {
      return false;
    }
  };

  class HtRewardLadder extends HTMLElement {
    connectedCallback() {
      this.base = Number(this.dataset.base) || 0;
      this.extra = 0;
      this.steps = [...this.querySelectorAll('[data-rl-tier]')];
      this.headline = this.querySelector('[data-rl-headline]');
      this.legend = this.querySelector('[data-rl-legend]');
      this.gains = this.querySelector('[data-rl-gains]');

      this.querySelectorAll('[data-rl-gift]').forEach((form) => this.initGift(form));
      if (this.dataset.variant === 'cart') this.popNewlyReached();

      if (this.dataset.variant === 'bundle' && typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
        // The bundle preview lives outside the cart, so it follows cart changes itself.
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
      }
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    // The cart re-renders after every change: only tiers that were not reached last time pop.
    popNewlyReached() {
      let before = null;
      try {
        before = JSON.parse(sessionStorage.getItem('ht-rl-reached') || 'null');
      } catch (error) {
        before = null;
      }
      const reached = this.steps.filter((step) => step.classList.contains('is-done')).map((step) => step.dataset.rlTier);
      if (Array.isArray(before)) {
        this.steps.forEach((step) => {
          if (reached.includes(step.dataset.rlTier) && !before.includes(step.dataset.rlTier)) this.pop(step);
        });
      }
      try {
        sessionStorage.setItem('ht-rl-reached', JSON.stringify(reached));
      } catch (error) {
        // No memory: no pop next time.
      }
    }

    pop(step) {
      step.classList.remove('is-popping');
      void step.offsetWidth;
      step.classList.add('is-popping');
      step.addEventListener('animationend', () => step.classList.remove('is-popping'), { once: true });
    }

    // Bundle builder: what the cart would look like with this bundle (cents, after the bundle's own discount).
    project(extraCents) {
      this.extra = Math.max(0, Number(extraCents) || 0);
      this.render();
    }

    // Reads the current cart, because the page may be older than the last add to cart.
    async refresh() {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
        });
        const cart = await response.json();
        // Same rule as the Liquid: the picker's gift line does not count, other gift product lines at full price.
        const giftProducts = this.dataset.giftProducts || ',';
        this.base = cart.items.reduce((sum, item) => {
          if (item.properties?._ht_reward) return sum;
          if (giftProducts.includes(`,${item.product_id},`)) return sum + item.original_line_price;
          return sum + item.final_line_price;
        }, 0);
        // Runs when the bundle dialog opens: let the badges animate in again.
        this.gains?.replaceChildren();
        this.render();
      } catch (error) {
        // Keep the server-rendered numbers.
      }
    }

    // Bundle preview: what the cart already has (soft tint) vs. what this bundle adds (accent),
    // so tiers reached by earlier cart items are never credited to the bundle.
    render() {
      const total = this.base + this.extra;
      const clamp = (value) => Math.max(0, Math.min(1, value));
      let next = null;
      const added = [];

      this.steps.forEach((step) => {
        const from = Number(step.dataset.from);
        const threshold = Number(step.dataset.threshold);
        const baseFill = clamp((this.base - from) / (threshold - from));
        const totalFill = clamp((total - from) / (threshold - from));
        const isHad = this.base >= threshold;
        const done = total >= threshold;
        if (done && !isHad) added.push(step);
        if (!done && !next) next = step;
        if (done && !step.classList.contains('is-done')) this.pop(step);
        step.classList.toggle('is-done', done);
        step.classList.toggle('is-had', isHad && this.extra > 0);
        step.classList.toggle('is-next', step === next);
        const [baseBar, extraBar] = step.querySelectorAll('.ht-rl__fill');
        if (baseBar) baseBar.style.width = `${Math.round((extraBar ? baseFill : totalFill) * 100)}%`;
        if (extraBar) extraBar.style.width = `${Math.round((totalFill - baseFill) * 100)}%`;
      });

      if (!this.headline) return;
      const rest = next
        ? `Noch <b>${money(Number(next.dataset.threshold) - total)}</b> ${escape(next.dataset.goal)}.`
        : '';

      if (this.dataset.variant !== 'bundle' || this.extra <= 0) {
        this.headline.hidden = !rest;
        this.headline.innerHTML = rest;
        return;
      }

      // Bundle: the headline only points to the next tier, what the bundle unlocks shows as badges.
      this.headline.hidden = !rest;
      this.headline.innerHTML = rest;
      if (this.legend) this.legend.hidden = this.base <= 0;
      this.renderGains(added);
    }

    // One badge per tier this bundle unlocks. Only badges that were not there before animate in.
    renderGains(added) {
      if (!this.gains) return;
      const wanted = added.map((step) => step.dataset.rlTier);
      [...this.gains.children].forEach((badge) => {
        if (!wanted.includes(badge.dataset.tier)) badge.remove();
      });
      let fresh = 0;
      added.forEach((step) => {
        const handle = step.dataset.rlTier;
        let badge = this.gains.querySelector(`[data-tier="${handle}"]`);
        if (!badge) {
          badge = document.createElement('li');
          badge.className = 'ht-rl__gain is-new';
          badge.dataset.tier = handle;
          badge.style.setProperty('--ht-rl-delay', `${fresh * 90}ms`);
          badge.innerHTML = `${ARROW}<span>${escape(step.dataset.title)}</span>`;
          badge.addEventListener('animationend', () => badge.classList.remove('is-new'), { once: true });
          fresh += 1;
        }
        this.gains.append(badge);
      });
    }

    initGift(form) {
      if (isDismissed(form.dataset.reward)) {
        form.hidden = true;
        return;
      }
      // Dawn's cart listens for every change inside it and would treat the radios as quantity inputs.
      form.addEventListener('change', (event) => {
        event.stopPropagation();
        this.updateChoice(form);
      });
      form.querySelector('[data-rl-add]')?.addEventListener('click', () => this.addGift(form));
      this.initSlider(form);
      form.querySelector('[data-rl-dismiss]')?.addEventListener('click', () => {
        try {
          sessionStorage.setItem(dismissKey(form.dataset.reward), '1');
        } catch (error) {
          // Hidden for this view only.
        }
        form.hidden = true;
      });
      this.updateChoice(form);
    }

    // Fades the edge where more bags are hidden, and shows the arrow for that side (mouse devices).
    initSlider(form) {
      const choices = form.querySelector('[data-rl-choices]');
      const slider = form.querySelector('[data-rl-slider]');
      if (!choices || !slider) return;

      // A side fades only while its outermost bag is not fully visible.
      const slides = slider.querySelectorAll('.ht-rl__slide');
      const first = slides[0];
      const last = slides[slides.length - 1];
      if (first && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const side = entry.target === first ? 'has-more-left' : 'has-more-right';
              choices.classList.toggle(side, entry.intersectionRatio < 0.98);
            });
          },
          { root: slider, threshold: [0.98, 1] }
        );
        observer.observe(first);
        if (last !== first) observer.observe(last);
      } else {
        const update = () => {
          const max = slider.scrollWidth - slider.clientWidth;
          choices.classList.toggle('has-more-left', slider.scrollLeft > 2);
          choices.classList.toggle('has-more-right', slider.scrollLeft < max - 2);
        };
        slider.addEventListener('scroll', update, { passive: true });
        update();
      }

      // Own easing instead of scrollBy({ behavior: 'smooth' }), which some browsers cut short or skip.
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let frame = null;
      const scrollTo = (target) => {
        cancelAnimationFrame(frame);
        const from = slider.scrollLeft;
        const to = Math.max(0, Math.min(target, slider.scrollWidth - slider.clientWidth));
        if (reduceMotion) {
          slider.scrollLeft = to;
          return;
        }
        const start = performance.now();
        const step = (now) => {
          const t = Math.min(1, (now - start) / 320);
          slider.scrollLeft = from + (to - from) * (1 - Math.pow(1 - t, 3));
          if (t < 1) frame = requestAnimationFrame(step);
        };
        frame = requestAnimationFrame(step);
      };
      choices.querySelectorAll('[data-rl-nav]').forEach((button) => {
        button.addEventListener('click', () => {
          scrollTo(slider.scrollLeft + Number(button.dataset.rlNav) * slider.clientWidth * 0.75);
        });
      });

      // Keep the chosen bag visible when it is picked by keyboard.
      slider.addEventListener('focusin', (event) => {
        event.target.closest('.ht-rl__slide')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    }

    updateChoice(form) {
      const checked = form.querySelector('input[type="radio"]:checked');
      const label = form.querySelector('[data-rl-choice]');
      if (label) label.textContent = checked?.dataset.title || '';
    }

    async addGift(form) {
      const button = form.querySelector('[data-rl-add]');
      const error = form.querySelector('[data-rl-error]');
      const variantId = form.querySelector('input[type="radio"]:checked')?.value;
      if (!variantId || button.disabled) return;

      const cartDrawer = this.closest('cart-drawer');
      const sections = cartDrawer?.getSectionsToRender?.().map((section) => section.id) || [];
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      error.hidden = true;

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1, properties: { _ht_reward: form.dataset.reward } }],
            ...(sections.length && { sections, sections_url: window.location.pathname }),
          }),
        });
        const state = await response.json();
        if (!response.ok || state.status) throw new Error(state.description || state.message || 'add failed');
        window.posthog?.capture?.('reward_gift_added', { reward: form.dataset.reward, variant_id: Number(variantId) });

        if (cartDrawer?.renderContents && state.sections) {
          cartDrawer.renderContents({ ...state, id: Number(variantId) });
        } else {
          // Cart page: Dawn's partial refresh would leave the footer totals stale.
          window.location.reload();
        }
      } catch (err) {
        error.textContent = 'Das hat leider nicht geklappt. Bitte versuche es noch einmal.';
        error.hidden = false;
        button.disabled = false;
      } finally {
        button.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('ht-reward-ladder', HtRewardLadder);
}
